import * as React from "react";
import type {CabanaConfiguration} from "./configuration/CabanaConfiguration";
import {Navigate, Route, Routes} from "react-router-dom";
import RedirectKeepingSearch from "./components/navigation/RedirectKeepingSearch";
import TransitioningContainer from "./components/TransitioningContainer";
import App from "./App";
import CustomersView, {prepare_CustomersView} from "./components/CustomersView";
import CustomerView, {prepare_CustomerView} from "./components/CustomerView";
import CustomerAccountView from "./components/CustomerAccountView";
import CustomerContactsView from "./components/CustomerContactsView";
import CustomerPaymentView from "./components/CustomerPaymentView";
import ContactsView, {prepare_ContactsView} from "./components/ContactsView";
import ContactView, {prepare_ContactView} from "./components/ContactView";
import InvoicesView, {prepare_InvoicesView} from "./components/InvoicesView";
import OutgoingInvoicesView from "./components/OutgoingInvoicesView";
import ConfigurationView, {prepare_ConfigurationView} from "./components/ConfigurationView";
import ConfigurationContractsView from "./components/ConfigurationContractsView";
import ConfigurationAttributeDefinitionsView from "./components/ConfigurationAttributeDefinitionsView";
import ConfigurationFinanceView from "./components/ConfigurationFinanceView";
import SubscriptionsView, {prepare_SubscriptionsView} from "./components/SubscriptionsView";
import SettlementsView, {prepare_SettlementsView} from "./components/SettlementsView";
import SettlementView, {prepare_SettlementView} from "./components/SettlementView";
import SubscriptionView, {prepare_SubscriptionView} from "./components/SubscriptionView";
import DraftInvoiceView from "./components/DraftInvoiceView";
import InvoiceView, {prepare_InvoiceView} from "./components/InvoiceView";
import FinalInvoiceView from "./components/FinalInvoiceView";
import {pdfjs} from 'react-pdf';
import ProjectsView, {prepare_ProjectsView} from "./components/ProjectsView";
import ProjectView, {prepare_ProjectView} from "./components/ProjectView";
import ProjectDetailsView from "./components/ProjectDetailsView";
import ProjectPaymentView from "./components/ProjectPaymentView";

import ContactContactView from "./components/ContactContactView";
import ContactActivityView from "./components/ContactActivityView";
import ContactDataProtectionView from "./components/ContactDataProtectionView";
import ContactMarketingView from "./components/ContactMarketingView";
import CustomerActivityView from "./components/CustomerActivityView";

import MarketingView, {prepare_MarketingView} from "./components/MarketingView";
import MarketingEmailsView from "./components/MarketingEmailsView";
import MarketingEmailView, {prepare_MarketingEmailView} from "./components/MarketingEmailView";
import MarketingEmailDetailsView from "./components/MarketingEmailDetailsView";
import MarketingEmailDeliveryView from "./components/MarketingEmailDeliveryView";
import MarketingEmailUnsubscribesView from "./components/MarketingEmailUnsubscribesView";
import MailingListsView from "./components/MailingListsView";
import MailingListView, {prepare_MailingListView} from "./components/MailingListView";
import MailingListDetailsView from "./components/MailingListDetailsView";
import EmailTemplatesView from "./components/EmailTemplatesView";
import EmailTemplateView, {prepare_EmailTemplateView} from "./components/EmailTemplateView";
import EmailTemplateDetailsView from "./components/EmailTemplateDetailsView";
import EmailLayoutsView from "./components/EmailLayoutsView";
import EmailLayoutView, {prepare_EmailLayoutView} from "./components/EmailLayoutView";
import EmailLayoutDetailsView from "./components/EmailLayoutDetailsView";

import AnalysisView, {prepare_AnalysisView} from "./components/AnalysisView";
import AnalysisIncomeView from "./components/AnalysisIncomeView";
import AnalysisExpenseOverviewView from "./components/AnalysisExpenseOverviewView";
import SuppliersView from "./components/SuppliersView";
import AnalysisForecastView, {prepare_AnalysisForecastView} from "./components/AnalysisForecastView";
import AnalysisForecastOverviewView from "./components/AnalysisForecastOverviewView";
import AnalysisForecastAccountsView from "./components/AnalysisForecastAccountsView";
import AnalysisForecastBwaView from "./components/AnalysisForecastBwaView";
import AnalysisForecastPlannedItemsView from "./components/AnalysisForecastPlannedItemsView";
import IncomingInvoicesView from "./components/IncomingInvoicesView";
import IncomingInvoiceView from "./components/IncomingInvoiceView";
import AnalysisOpenItemsView from "./components/AnalysisOpenItemsView";
import AnalysisSimulationView from "./components/AnalysisSimulationView";
import AnalysisServerCostView from "./components/AnalysisServerCostView";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type RootProps = {
    configuration: CabanaConfiguration
}

const Root: React.FC<RootProps> = ({configuration}) => {
    return (
        <Routes>
            <Route path="/" element={<App/>}>
                <Route index element={<TransitioningContainer component={CustomersView} prepare={prepare_CustomersView}/>}/>

                <Route path="customers" element={<TransitioningContainer component={CustomersView} prepare={prepare_CustomersView}/>}/>
                <Route path="customers/:id" element={<TransitioningContainer component={CustomerView} prepare={prepare_CustomerView}/>}>
                    <Route index element={<CustomerAccountView/>}/>
                    <Route path="account" element={<CustomerAccountView/>}/>
                    <Route path="activities" element={<CustomerActivityView/>}/>
                    <Route path="contacts" element={<CustomerContactsView/>}/>
                    <Route path="payment" element={<CustomerPaymentView/>}/>
                </Route>

                <Route path="contacts" element={<TransitioningContainer component={ContactsView} prepare={prepare_ContactsView}/>}/>
                <Route path="contacts/:id" element={<TransitioningContainer component={ContactView} prepare={prepare_ContactView}/>}>
                    <Route index element={<ContactContactView/>}/>
                    <Route path="details" element={<ContactContactView/>}/>
                    <Route path="activities" element={<ContactActivityView/>}/>
                    <Route path="data-protection" element={<ContactDataProtectionView/>}/>
                    <Route path="marketing" element={<ContactMarketingView/>}/>
                </Route>

                <Route path="projects" element={<TransitioningContainer component={ProjectsView} prepare={prepare_ProjectsView}/>}/>
                <Route path="projects/:id" element={<TransitioningContainer component={ProjectView} prepare={prepare_ProjectView}/>}>
                    <Route index element={<ProjectDetailsView/>}/>
                    <Route path="details" element={<ProjectDetailsView/>}/>
                    <Route path="payment" element={<ProjectPaymentView/>}/>
                </Route>

                <Route path="subscriptions" element={<TransitioningContainer component={SubscriptionsView} prepare={prepare_SubscriptionsView}/>}/>
                <Route path="subscriptions/:id" element={<TransitioningContainer component={SubscriptionView} prepare={prepare_SubscriptionView}/>}>
                    <Route index element={<SubscriptionView prepared={prepare_SubscriptionView}/>}/>
                </Route>

                <Route path="settlements" element={<TransitioningContainer component={SettlementsView} prepare={prepare_SettlementsView}/>}/>
                <Route path="settlements/:id" element={<TransitioningContainer component={SettlementView} prepare={prepare_SettlementView}/>}>
                    <Route index element={<SettlementView prepared={prepare_SettlementView}/>}/>
                    <Route path=":language" element={<SettlementView prepared={prepare_SettlementView}/>}/>
                </Route>

                <Route path="invoices" element={<TransitioningContainer component={InvoicesView} prepare={prepare_InvoicesView}/>}>
                    <Route index element={<RedirectKeepingSearch to="/invoices/outgoing"/>}/>
                    <Route path="outgoing" element={<OutgoingInvoicesView/>}/>
                    <Route path="incoming" element={<IncomingInvoicesView/>}/>
                    <Route path="incoming/:fastBillInvoiceId" element={<IncomingInvoiceView/>}/>
                    <Route path="suppliers" element={<SuppliersView/>}/>
                </Route>
                <Route path="invoices/:id" element={<TransitioningContainer component={InvoiceView} prepare={prepare_InvoiceView}/>}>
                    <Route index element={<FinalInvoiceView/>}/>
                    <Route path="edit" element={<DraftInvoiceView/>}/>
                </Route>

                <Route path="configuration" element={<TransitioningContainer component={ConfigurationView} prepare={prepare_ConfigurationView}/>}>
                    <Route index element={<ConfigurationContractsView/>}/>
                    <Route path="contracts" element={<ConfigurationContractsView/>}/>
                    <Route path="attributes" element={<ConfigurationAttributeDefinitionsView/>}/>
                    <Route path="finance" element={<ConfigurationFinanceView/>}/>
                </Route>

                {/* Analysis section with sub-navigation */}
                <Route path="analysis" element={<TransitioningContainer component={AnalysisView} prepare={prepare_AnalysisView}/>}>
                    <Route index element={<Navigate to="income" replace/>}/>
                    <Route path="income" element={<AnalysisIncomeView/>}/>
                    <Route path="expenses" element={<AnalysisExpenseOverviewView/>}/>
                    <Route path="expenses/overview" element={<RedirectKeepingSearch to="/analysis/expenses"/>}/>
                    {/* Legacy paths of incoming invoices and suppliers */}
                    <Route path="expenses/invoices" element={<RedirectKeepingSearch to="/invoices/incoming"/>}/>
                    <Route path="expenses/invoices/:fastBillInvoiceId" element={<RedirectKeepingSearch to="/invoices/incoming/:fastBillInvoiceId"/>}/>
                    <Route path="expenses/suppliers" element={<RedirectKeepingSearch to="/invoices/suppliers"/>}/>
                    <Route path="forecast" element={<TransitioningContainer component={AnalysisForecastView} prepare={prepare_AnalysisForecastView}/>}>
                        <Route index element={<Navigate to="overview" replace/>}/>
                        <Route path="overview" element={<AnalysisForecastOverviewView/>}/>
                        <Route path="accounts" element={<AnalysisForecastAccountsView/>}/>
                        <Route path="bwa" element={<AnalysisForecastBwaView/>}/>
                        <Route path="planned-items" element={<AnalysisForecastPlannedItemsView/>}/>
                    </Route>
                    <Route path="simulation" element={<AnalysisSimulationView/>}/>
                    <Route path="servers" element={<AnalysisServerCostView/>}/>
                    <Route path="openitems" element={<AnalysisOpenItemsView/>}/>
                </Route>

                {/* Marketing section with sub-navigation */}
                <Route path="marketing" element={<TransitioningContainer component={MarketingView} prepare={prepare_MarketingView}/>}>
                    <Route index element={<MarketingEmailsView/>}/>
                    <Route path="emails" element={<MarketingEmailsView/>}/>
                    <Route path="lists" element={<MailingListsView/>}/>
                    <Route path="templates" element={<EmailTemplatesView/>}/>
                    <Route path="layouts" element={<EmailLayoutsView/>}/>
                </Route>

                {/* Marketing email detail view */}
                <Route path="marketing/emails/:id" element={<TransitioningContainer component={MarketingEmailView} prepare={prepare_MarketingEmailView}/>}>
                    <Route index element={<MarketingEmailDetailsView/>}/>
                    <Route path="delivery" element={<MarketingEmailDeliveryView/>}/>
                    <Route path="unsubscribes" element={<MarketingEmailUnsubscribesView/>}/>
                </Route>

                {/* Mailing list detail view */}
                <Route path="marketing/lists/:id" element={<TransitioningContainer component={MailingListView} prepare={prepare_MailingListView}/>}>
                    <Route index element={<MailingListDetailsView/>}/>
                </Route>

                {/* Email template detail view */}
                <Route path="marketing/templates/:id" element={<TransitioningContainer component={EmailTemplateView} prepare={prepare_EmailTemplateView}/>}>
                    <Route index element={<EmailTemplateDetailsView/>}/>
                </Route>

                {/* Email layout detail view */}
                <Route path="marketing/layouts/:id" element={<TransitioningContainer component={EmailLayoutView} prepare={prepare_EmailLayoutView}/>}>
                    <Route index element={<EmailLayoutDetailsView/>}/>
                </Route>
            </Route>
        </Routes>
    );
}

export {CabanaConfiguration};
export default Root;
